<script setup lang="ts">
import { computed, ref } from 'vue'

import PresetColorSelect from '../inputs/PresetColorSelect.vue'

import { useShiftPresetUpdateMutation } from '../../composables/useShiftPreset'

import type { SiteID } from '../../../sites/models/site'
import { ShiftPreset } from '../../models/shiftPreset'

import { ShiftPresetUpdateForm } from '../../shiftPresetService'

interface FormData {
  name: string
  colorHex: string
  startTime: string
  endTime: string
}
type InitialData = Partial<FormData>

const { siteId, shiftPreset } = defineProps<{
  siteId: SiteID
  shiftPreset: ShiftPreset
}>()

const createForm = (
  shiftPreset: ShiftPreset,
  initialData?: InitialData,
): FormData => {
  return {
    name: initialData?.name ?? shiftPreset.name,
    colorHex: initialData?.colorHex ?? shiftPreset.colorHex,
    startTime: initialData?.startTime ?? shiftPreset.startTime,
    endTime: initialData?.endTime ?? shiftPreset.endTime,
  }
}

const form = ref<FormData>(createForm(shiftPreset))

const { mutate: updateMutate, isPending: isUpdatePending } =
  useShiftPresetUpdateMutation(() => siteId)
const update = (shiftPreset: ShiftPreset) => {
  const updateForm: ShiftPresetUpdateForm = {}

  if (form.value.name !== shiftPreset.name) {
    updateForm.name = form.value.name
  }

  // PresetColorSelect keeps "#RRGGBB", as the API takes it
  if (form.value.colorHex !== shiftPreset.colorHex) {
    updateForm.colorHex = form.value.colorHex
  }

  if (form.value.startTime !== shiftPreset.startTime) {
    updateForm.startTime = form.value.startTime
  }

  if (form.value.endTime !== shiftPreset.endTime) {
    updateForm.endTime = form.value.endTime
  }

  updateMutate({
    shiftPresetId: shiftPreset.id,
    form: updateForm,
  })
}

const hasChanges = computed(
  () =>
    form.value.name !== shiftPreset.name ||
    form.value.colorHex !== shiftPreset.colorHex ||
    form.value.startTime !== shiftPreset.startTime ||
    form.value.endTime !== shiftPreset.endTime,
)
const isSaveDisabled = computed(() => !hasChanges.value)
</script>

<template>
  <div class="flex flex-col gap-y-4 m-1">
    <Message v-if="false">
      <div class="flex flex-col gap-4">
        <div class="text-color font-semibold">
          {{ shiftPreset.name }}
        </div>

        <div class="text-color font-medium">shiftPreset.description</div>

        <Button :label="'Help Link'" link class="w-fit p-0" />
      </div>
    </Message>

    <div class="flex flex-col gap-2">
      <label for="area-detail-name-input">Name</label>
      <InputText
        id="area-detail-name-input"
        v-model="form.name"
        aria-describedby="area-detail-name-help"
      />
    </div>

    <div class="flex flex-col items-start gap-2">
      <span>Colour</span>
      <PresetColorSelect v-model="form.colorHex" />
    </div>

    <div class="flex flex-col gap-2">
      <label for="area-detail-start-time-input">Start Time</label>
      <InputText id="area-detail-start-time-input" v-model="form.startTime" />
    </div>

    <div class="flex flex-col gap-2">
      <label for="area-detail-end-time-input">End Time</label>
      <InputText id="area-detail-end-time-input" v-model="form.endTime" />
    </div>

    <Button
      :label="'Save'"
      class="w-fit self-end"
      :loading="isUpdatePending"
      :disabled="isSaveDisabled"
      @click="update(shiftPreset)"
    />
  </div>
</template>
